import { useEffect, useRef } from 'react';
import { apiFetch } from '../lib/apiClient';

export interface WatchIncident {
  id: string;
  incidentNumber: string;
  title: string;
  severity: string;
  status: string;
  createdDateTime: string;
  webUrl: string;
}

export interface WatchCustomerEntry {
  customerId: string;
  customerName: string;
  color: string;
  incidents: WatchIncident[];
  error?: string;
}

interface WatchPayload {
  fetchedAt: string;
  customers: WatchCustomerEntry[];
}

export interface NewIncidentEvent {
  customerId: string;
  customerName: string;
  customerColor: string;
  incident: WatchIncident;
  /** True if the document was hidden when this incident was detected. */
  detectedWhileHidden: boolean;
}

export interface UseIncidentWatcherOptions {
  /** Polling interval in seconds while the tab is visible. Default 30s. Set <=0 to disable. */
  intervalSeconds?: number;
  /**
   * Polling interval in seconds while the tab is hidden. Default 60s.
   * Critical for SLA safety — without background polling we can miss the
   * 5-minute Critical TTA whenever the analyst tabs away. Set to 0 to
   * preserve the legacy "pause on hidden" behavior.
   */
  backgroundIntervalSeconds?: number;
  /** Called once per newly-detected incident (after baseline is established). */
  onNewIncident: (event: NewIncidentEvent) => void;
  /** Called when an incident transitions out of 'New' status (acknowledged by any analyst). */
  onIncidentAcknowledged?: (event: { customerId: string; incidentNumber: string }) => void;
  /** When false, the watcher is paused (e.g. Azure not configured yet). */
  enabled?: boolean;
}

const SEEN_MAX = 5000;

/**
 * Polls /incidents/global-watch for the latest incidents across all enabled
 * customers and fires `onNewIncident` for each incidentNumber that appears
 * after the initial baseline poll.
 *
 * Polling continues while the tab is hidden (at a slower cadence) so an
 * analyst who switches to email or Teams can still be alerted via the Web
 * Notifications API before SLA budget is consumed.
 *
 * The first successful poll is treated as the baseline — no toasts fire for
 * incidents that already existed when the app loaded; only genuinely-new
 * incidents (created after the watcher started) trigger notifications.
 */
export function useIncidentWatcher({
  intervalSeconds = 30,
  backgroundIntervalSeconds = 60,
  onNewIncident,
  onIncidentAcknowledged,
  enabled = true,
}: UseIncidentWatcherOptions): void {
  // Set of `${customerId}::${incidentNumber}` keys we've already seen.
  const seenRef = useRef<Set<string>>(new Set());
  // First successful poll seeds the baseline silently.
  const baselineSetRef = useRef<boolean>(false);
  // Guard against polling overlap: skip a tick rather than stack requests.
  const inFlightRef = useRef<boolean>(false);
  const onNewIncidentRef = useRef(onNewIncident);
  useEffect(() => { onNewIncidentRef.current = onNewIncident; }, [onNewIncident]);
  const incidentStatusRef = useRef<Map<string, string>>(new Map());
  const onAcknowledgedRef = useRef(onIncidentAcknowledged);
  useEffect(() => { onAcknowledgedRef.current = onIncidentAcknowledged; }, [onIncidentAcknowledged]);

  useEffect(() => {
    if (!enabled || intervalSeconds <= 0) return;

    // Best-effort permission request for OS-level alerts when tab is hidden.
    // We don't gate the watcher on this — toasts still fire when visible —
    // but if the analyst grants it once, we can ping them even when the
    // dashboard isn't the focused tab. Wrapped in try/catch because some
    // browsers throw on the call rather than returning 'denied'.
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try { void Notification.requestPermission(); } catch { /* ignore */ }
    }

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    let currentIntervalMs = intervalSeconds * 1000;

    async function poll(): Promise<void> {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const data = await apiFetch<WatchPayload>('/incidents/global-watch');
        if (cancelled) return;
        // Sample visibility AFTER the fetch resolves — that's the moment the
        // user would actually receive the notification. Sampling at poll
        // start could mis-classify a fetch that began in the foreground but
        // resolved after the user tabbed away (and vice versa).
        const isHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';

        const newEvents: NewIncidentEvent[] = [];
        for (const cust of data.customers ?? []) {
          for (const inc of cust.incidents ?? []) {
            const key = `${cust.customerId}::${inc.incidentNumber}`;
            if (seenRef.current.has(key)) continue;
            if (seenRef.current.size >= SEEN_MAX) {
              const oldest = seenRef.current.values().next().value;
              if (oldest !== undefined) seenRef.current.delete(oldest);
            }
            seenRef.current.add(key);
            if (baselineSetRef.current) {
              newEvents.push({
                customerId: cust.customerId,
                customerName: cust.customerName,
                customerColor: cust.color,
                incident: inc,
                detectedWhileHidden: isHidden,
              });
            }
          }
        }

        for (const cust of data.customers ?? []) {
          for (const inc of cust.incidents ?? []) {
            const key = `${cust.customerId}::${inc.incidentNumber}`;
            const prev = incidentStatusRef.current.get(key);
            incidentStatusRef.current.set(key, inc.status);
            if (baselineSetRef.current && prev === 'New' && inc.status !== 'New') {
              onAcknowledgedRef.current?.({ customerId: cust.customerId, incidentNumber: inc.incidentNumber });
            }
          }
        }

        for (const cust of data.customers ?? []) {
          for (const inc of cust.incidents ?? []) {
            const key = `${cust.customerId}::${inc.incidentNumber}`;
            const prev = incidentStatusRef.current.get(key);
            incidentStatusRef.current.set(key, inc.status);
            if (baselineSetRef.current && prev === 'New' && inc.status !== 'New') {
              onAcknowledgedRef.current?.({ customerId: cust.customerId, incidentNumber: inc.incidentNumber });
            }
          }
        }

        for (const cust of data.customers ?? []) {
          for (const inc of cust.incidents ?? []) {
            const key = `${cust.customerId}::${inc.incidentNumber}`;
            const prev = incidentStatusRef.current.get(key);
            incidentStatusRef.current.set(key, inc.status);
            if (baselineSetRef.current && prev === 'New' && inc.status !== 'New') {
              onAcknowledgedRef.current?.({ customerId: cust.customerId, incidentNumber: inc.incidentNumber });
            }
          }
        }

        if (!baselineSetRef.current) {
          baselineSetRef.current = true;
        } else {
          // Cap the burst — sort newest-first by createdDateTime, keep at most 5.
          newEvents.sort((a, b) =>
            (b.incident.createdDateTime || '').localeCompare(a.incident.createdDateTime || '')
          );
          for (const ev of newEvents.slice(0, 5)) {
            onNewIncidentRef.current(ev);
          }
        }
      } catch {
        // Silent: the dashboard's main fetch will surface a banner if API is down.
      } finally {
        inFlightRef.current = false;
      }
    }

    function applyInterval(targetMs: number): void {
      if (timer && currentIntervalMs === targetMs) return;
      currentIntervalMs = targetMs;
      if (timer) clearInterval(timer);
      timer = setInterval(() => { void poll(); }, targetMs);
    }

    function stopTimer(): void {
      if (timer) { clearInterval(timer); timer = null; }
    }

    // Kick off immediately so baseline is established without waiting.
    void poll();

    // Decide initial cadence based on current visibility.
    const initiallyHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    if (initiallyHidden && backgroundIntervalSeconds > 0) {
      applyInterval(backgroundIntervalSeconds * 1000);
    } else if (!initiallyHidden) {
      applyInterval(intervalSeconds * 1000);
    }
    // (else: hidden + background disabled — leave timer null, will start on visibility)

    function onVisibility(): void {
      if (document.visibilityState === 'visible') {
        // Coming back: catch up immediately, then resume foreground cadence.
        void poll();
        applyInterval(intervalSeconds * 1000);
      } else if (backgroundIntervalSeconds > 0) {
        // Going to background: keep polling at the slower rate so we still
        // catch incidents and can surface them via Notifications API.
        applyInterval(backgroundIntervalSeconds * 1000);
      } else {
        // Legacy opt-out: caller asked us to stop polling when hidden.
        stopTimer();
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      stopTimer();
    };
  }, [enabled, intervalSeconds, backgroundIntervalSeconds]);
}
